import React from 'react';
import { Formik } from 'formik';
import Button from 'components/customDesign/Button';
import { ReactComponent as SaveIcon } from 'assets/icons/save_circle.svg';
import { ReactComponent as ProfilIcon } from 'assets/icons/profile_circle.svg';
import { getFirstLetters } from 'services/helpers/user';
import erlClient from 'services/clients';
import { retrieveTeamMember } from 'ducks/user';
import { useDispatch } from 'react-redux';
import { Dialog, IconButton, ListItem, ListItemText } from '@mui/material';
import { XMarkIcon } from '@heroicons/react/24/solid';
import fonts from 'styles/typography.module.css';
import { Profile } from 'types/User';
import UserRightField from './UserRightField';
import ValidateUserDelete from './ValidateUserDelete';
import UserAvatar from './UserAvatar';

interface Props {
  isDialogOpen: boolean;
  closeDialog: () => void;
  selectedMember: Profile;
}

const EditUserRightDialog: React.FC<Props> = (props) => {
  const { isDialogOpen, closeDialog, selectedMember } = props;
  const initialValues = {
    is_admin: selectedMember && selectedMember.is_admin,
    email: selectedMember && selectedMember.email,
  };

  const dispatch = useDispatch();
  const submitCallback = () => dispatch(retrieveTeamMember());

  return (
    <Dialog
      open={isDialogOpen}
      onClose={closeDialog}
      className={fonts.font}
      classes={{ paper: 'flex flex-col p-8 items-center gap-4 relative' }}
    >
      <div className="absolute top-0 right-0">
        <IconButton onClick={closeDialog}>
          <XMarkIcon className="h-5" />
        </IconButton>
      </div>
      <ProfilIcon className="flex-shrink-0" />
      <h1 className="text-center font-bold">Access</h1>
      <h5 className="text-gray-500">Edit the level of access for a member</h5>
      {selectedMember && (
        <Formik
          initialValues={initialValues}
          onSubmit={async (values, { setSubmitting, setErrors }) => {
            try {
              await erlClient.put('/core/api/v1/auth/company/admins', values);
              submitCallback?.();
              closeDialog();
              setSubmitting(false);
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (e: any) {
              setSubmitting(false);
              setErrors(e.response.data);
            }
          }}
        >
          {({ values, handleChange, handleSubmit, isSubmitting, errors }) => (
            <form onSubmit={handleSubmit} className="flex flex-col items-center">
              <div className="bg-gray-100 p-4">
                <ListItem>
                  <UserAvatar size="small" letters={getFirstLetters(selectedMember)} />
                  <ListItemText
                    classes={{
                      root: 'ml-2',
                    }}
                    primary={`${selectedMember.first_name} ${selectedMember.last_name}`}
                    secondary={selectedMember.is_admin ? 'Admin' : 'Member'}
                  />
                </ListItem>
                <UserRightField
                  value={values.is_admin}
                  name="is_admin"
                  handleChange={handleChange}
                />
                <h4 className="text-red-600">{errors.email || errors.is_admin}</h4>
                <ValidateUserDelete
                  selectedMember={selectedMember}
                  submitCallback={() => {
                    closeDialog();
                    submitCallback?.();
                  }}
                />
              </div>
              <Button
                type="submit"
                rightIcon={<SaveIcon className="ml-2" />}
                isLoading={isSubmitting}
              >
                Save
              </Button>
            </form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};

export default EditUserRightDialog;
